import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ManagerSidebar from '../components/Sidebar/ManagerSidebar';
import "./AttendanceForm.css";

const API_BASE_URL = 'https://ikaze-thouse-backend.vercel.app/api/v1';

const AttendanceForm = ({ onSuccess }) => {
    const [guards, setGuards] = useState([]);
    const [guardsLoading, setGuardsLoading] = useState(true);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        guard_id: '',
        date: new Date().toISOString().split('T')[0],
        shift_type: 'DAY',
        check_in: '',
        check_out: '',
        late_minutes: 0,
        rating: 5
    });

    // Format Late Time
    const formatLateTime = (totalMinutes) => {
        if (totalMinutes <= 0) return "On Time";

        if (totalMinutes < 60) {
            return `${totalMinutes} mins`;
        }

        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        return mins > 0
            ? `${hours}h ${mins}m`
            : `${hours}h`;
    };

    // Auto Calculate Rating & Late Minutes
    useEffect(() => {
        if (!formData.check_in) return;

        const [hours, minutes] =
            formData.check_in.split(':').map(Number);

        const actualMinutes = hours * 60 + minutes;

        const expectedMinutes =
            formData.shift_type === 'DAY'
                ? 360
                : 1080;

        let delay = actualMinutes - expectedMinutes;

        // Handle Night Shift After Midnight
        if (
            formData.shift_type === 'NIGHT' &&
            hours < 12
        ) {
            delay =
                (actualMinutes + 1440) - expectedMinutes;
        }

        const lateMins = Math.max(0, delay);

        let autoRating = 5.0;

        if (lateMins === 0) autoRating = 5.0;
        else if (lateMins <= 15) autoRating = 4.5;
        else if (lateMins <= 30) autoRating = 4.0;
        else if (lateMins <= 60) autoRating = 3.0;
        else if (lateMins <= 120) autoRating = 2.0;
        else autoRating = 1.0;

        setFormData((prev) => ({
            ...prev,
            late_minutes: lateMins,
            rating: autoRating
        }));
    }, [formData.check_in, formData.shift_type]);

    // Fetch Guards
    useEffect(() => {
        const fetchGuards = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE_URL}/guards/`
                );

                const data =
                    res.data?.guards ||
                    res.data?.data ||
                    res.data ||
                    [];

                setGuards(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching guards:", err);
            } finally {
                setGuardsLoading(false);
            }
        };

        fetchGuards();
    }, []);

    // Submit Form
    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);

        const payload = {
            guard_id: formData.guard_id,
            date: formData.date,
            shift_type: formData.shift_type,

            check_in: formData.check_in
                ? `${formData.date}T${formData.check_in}:00Z`
                : null,

            check_out: formData.check_out
                ? `${formData.date}T${formData.check_out}:00Z`
                : null,

            late_minutes: Number(formData.late_minutes),
            rating: Number(formData.rating)
        };

        try {
            await axios.post(
                `${API_BASE_URL}/attendance`,
                payload
            );

            alert(
                `Success! Rating of ${formData.rating} stored.`
            );

            setFormData({
                guard_id: '',
                date: new Date().toISOString().split('T')[0],
                shift_type: 'DAY',
                check_in: '',
                check_out: '',
                late_minutes: 0,
                rating: 5
            });

            if (onSuccess) onSuccess();

        } catch (err) {
            console.error(
                "Submission error:",
                err.response?.data
            );

            alert(
                "Error: " +
                    (err.response?.data?.error ||
                        "Server Error")
            );

        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="attendance-container">

            <ManagerSidebar />

            <div className="attendance-card">

                <form onSubmit={handleSubmit}>

                    <h2 className="attendance-title">
                        Attendance Log
                    </h2>

                    <div className="attendance-info-banner">
                        Shift Starts:
                        <b> Day 06:00 </b> |
                        <b> Night 18:00 </b>
                    </div>

                    {/* Guard */}
                    <div className="attendance-form-group">

                        <label>Guard</label>

                        <select
                            required
                            value={formData.guard_id}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    guard_id: e.target.value
                                })
                            }
                        >
                            <option value="">
                                Select a guard...
                            </option>

                            {!guardsLoading &&
                                guards.map((g) => (
                                    <option
                                        key={g.id}
                                        value={g.id}
                                    >
                                        {g.name}
                                    </option>
                                ))}
                        </select>
                    </div>

                    {/* Shift + Date */}
                    <div className="attendance-row">

                        <div className="attendance-form-group">

                            <label>Shift</label>

                            <select
                                value={formData.shift_type}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        shift_type:
                                            e.target.value
                                    })
                                }
                            >
                                <option value="DAY">
                                    Day Shift
                                </option>

                                <option value="NIGHT">
                                    Night Shift
                                </option>
                            </select>
                        </div>

                        <div className="attendance-form-group">

                            <label>Date</label>

                            <input
                                type="date"
                                value={formData.date}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        date: e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    {/* Check In + Out */}
                    <div className="attendance-row">

                        <div className="attendance-form-group">

                            <label>Check In</label>

                            <input
                                type="time"
                                required
                                value={formData.check_in}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        check_in:
                                            e.target.value
                                    })
                                }
                            />
                        </div>

                        <div className="attendance-form-group">

                            <label>Check Out</label>

                            <input
                                type="time"
                                value={formData.check_out}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        check_out:
                                            e.target.value
                                    })
                                }
                            />
                        </div>
                    </div>

                    {/* Late Minutes */}
                    <div className="attendance-form-group">

                        <label>Lateness Duration</label>

                        <input
                            type="text"
                            value={formatLateTime(
                                formData.late_minutes
                            )}
                            readOnly
                            style={{
                                color:
                                    formData.late_minutes > 0
                                        ? '#dc2626'
                                        : '#16a34a'
                            }}
                        />
                    </div>

                    {/* Rating */}
                    <div className="attendance-form-group">

                        <label>
                            Performance Rating
                        </label>

                        <input
                            type="text"
                            value={formData.rating}
                            readOnly
                        />

                        <div className="attendance-rating-badge">

                            {formData.rating >= 4
                                ? "⭐ Good"
                                : formData.rating >= 3
                                ? "⚠️ Average"
                                : "🚫 Poor"}

                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        className="attendance-btn"
                        type="submit"
                        disabled={loading}
                    >
                        {loading
                            ? "Saving..."
                            : "Submit Attendance"}
                    </button>

                </form>
            </div>
        </div>
    );
};

export default AttendanceForm;